import { SubmeshData } from '@models/classes/features/submesh-feature';
import { WeldVerticesFeature } from '@models/classes/features/weld-vertices-feature';
import { Token } from '@models/classes/token';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { FeatureRenderer } from './feature';

export class WeldVerticesRenderer extends FeatureRenderer<WeldVerticesFeature> {
    modelToken = Token.WeldVerticesFeature;
    updateMeshGeometry(
        feature: WeldVerticesFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        const parentMesh = feature._parent;
        const indices = newVertexData.indices;
        let positions = newVertexData.positions;
        let normals = newVertexData.normals;
        let uvs = newVertexData.uvs;
        const positionAverage: number[] = [0, 0, 0];
        const normalsAverage: number[] = [0, 0, 0];
        const uvAverage: number[] = [0, 0];
        const selectedIndices: number[] = [];
        const selectedVertices: number[] = [];
        const selectedIndicesArray: boolean[] = [];
        const vertexVisited: boolean[] = [];
        const indicesResult: number[] = [];
        const submeshDataArray: SubmeshData[] = [];

        if (parentMesh && newVertexData.positions && newVertexData.positions.length > 0) {
            if (newVertexData.positions) {
                positions = newVertexData.positions as number[];
            } else if (vertexData.positions) {
                positions = Array.from(vertexData.positions);
            } else {
                positions = [];
            }

            if (newVertexData.normals) {
                normals = newVertexData.normals as number[];
            } else if (vertexData.normals) {
                normals = Array.from(vertexData.normals);
            }

            if (newVertexData.uvs) {
                uvs = newVertexData.uvs as number[];
            } else if (vertexData.uvs) {
                uvs = Array.from(vertexData.uvs);
            }

            if (feature.vertexIndices && feature.vertexIndices.length > 0) {
                //creating sparse array to see which vertices are selected
                for (let i = 0; i < feature.vertexIndices.length; i++) {
                    selectedIndicesArray[feature.vertexIndices[i]] = true;
                }

                if (indices) {
                    //Looping through indices and checking which indices are touched by the selected vertices
                    for (let j = 0; j < indices?.length; j++) {
                        if (selectedIndicesArray[indices[j]]) {
                            selectedIndices.push(j);
                            if (!vertexVisited[indices[j]]) {
                                selectedVertices.push(j);
                                vertexVisited[indices[j]] = true;
                            }
                        }
                    }
                }

                //averaging all selected positions of the vertex
                for (let i = 0; i < selectedVertices.length; i++) {
                    if (indices) {
                        const vertex = indices[selectedVertices[i]];
                        for (let j = 0; j < 3; j++) {
                            positionAverage[j] = positionAverage[j] + positions[vertex * 3 + j];
                            if (normals) {
                                normalsAverage[j] = normalsAverage[j] + normals[vertex * 3 + j];
                            }
                            if (j != 2 && uvs) {
                                uvAverage[j] = uvAverage[j] + uvs[vertex * 2 + j];
                            }
                        }
                    }
                }
                for (let i = 0; i < 3; i++) {
                    positionAverage[i] = positionAverage[i] / selectedVertices.length;
                    normalsAverage[i] = normalsAverage[i] / selectedVertices.length;
                    if (i != 2) {
                        uvAverage[i] = uvAverage[i] / selectedVertices.length;
                    }
                }

                //setting one vertex to calculated average position
                if (indices) {
                    const targetIndex = indices[selectedVertices[0]];
                    for (let i = 0; i < 3; i++) {
                        positions[targetIndex * 3 + i] = positionAverage[i];
                        if (normals) {
                            normals[targetIndex * 3 + i] = normalsAverage[i];
                        }
                        if (i != 2 && uvs) {
                            uvs[targetIndex * 2 + i] = uvAverage[i];
                        }
                    }

                    //setting all selectedIndices to targetIndex with new position
                    for (let i = 0; i < selectedIndices.length; i++) {
                        indices[selectedIndices[i]] = targetIndex;
                    }

                    //looping through indices and pruning out polygons that make up invalid geometry
                    for (let i = 0; i < indices.length; i = i + 3) {
                        if (
                            indices[i] != indices[i + 1] &&
                            indices[i] != indices[i + 2] &&
                            indices[i + 1] != indices[i + 2]
                        ) {
                            indicesResult.push(indices[i]);
                            indicesResult.push(indices[i + 1]);
                            indicesResult.push(indices[i + 2]);
                        }
                    }

                    //handling submesh data

                    if (vertexData.submeshData) {
                        for (let i = 0; i < vertexData.submeshData.length; i++) {
                            const submeshData = vertexData.submeshData[i];
                            submeshData.indexCount = indicesResult.length;
                            submeshDataArray[i] = submeshData;
                        }
                    }
                }
            }
            newVertexData.positions = positions;
            newVertexData.indices = indicesResult;
            newVertexData.uvs = uvs;
            newVertexData.normals = normals;
            newVertexData.submeshData = submeshDataArray;
        }
        return newVertexData;
    }

    update3dInternal() {}
}
